import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BookOpen, Bot, KeyRound, LogIn, LogOut, Shield } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import AddMapleKeyDialog from '@/components/maple-market/AddMapleKeyDialog';

type UserActionsProps = {
  variant?: 'app' | 'landing';
  className?: string;
};

export default function UserActions({ variant = 'app', className }: UserActionsProps) {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const [addKeyOpen, setAddKeyOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const from = `${location.pathname}${location.search}${location.hash}`;

  const goToAuth = () => {
    navigate('/auth', { state: { from } });
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  if (!user) {
    if (variant === 'landing') {
      return (
        <button
          type="button"
          onClick={goToAuth}
          className={className ?? 'font-body text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground'}
        >
          Login
        </button>
      );
    }

    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={goToAuth}
        className={className ?? 'flex items-center gap-2'}
      >
        <LogIn className="h-4 w-4" />
        <span>Login</span>
      </Button>
    );
  }

  const displayName = profile?.name || user.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const isAdmin = (profile?.role || '').toLowerCase() === 'admin';

  const trigger = variant === 'landing' ? (
    <button
      type="button"
      className={className ?? 'inline-flex items-center'}
      aria-label="Open user menu"
    >
      <Avatar className="h-9 w-9 border border-white/15 bg-card">
        <AvatarImage src={profile?.avatar} alt={displayName} />
        <AvatarFallback className="bg-card text-foreground">{initials || <Bot className="h-5 w-5" />}</AvatarFallback>
      </Avatar>
    </button>
  ) : (
    <Button variant="ghost" size="icon" className={className ?? 'rounded-full'} aria-label="Open user menu">
      <Avatar className="h-8 w-8 bg-muted">
        <AvatarImage src={profile?.avatar} alt={displayName} />
        <AvatarFallback className="bg-background">{initials || <Bot className="h-5 w-5 text-foreground" />}</AvatarFallback>
      </Avatar>
    </Button>
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-56">
          <div className="px-2 py-1.5">
            <div className="text-sm font-medium text-foreground">{displayName}</div>
            <div className="text-xs text-muted-foreground">{user.email}</div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/profile')}>Profile</DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/market/library')}>
            <BookOpen className="mr-2 h-4 w-4" />
            Maple Market Library
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => setAddKeyOpen(true)}>
            <KeyRound className="mr-2 h-4 w-4" />
            Add Maple Key
          </DropdownMenuItem>
          {isAdmin && (
            <DropdownMenuItem onClick={() => navigate('/admin')}>
              <Shield className="mr-2 h-4 w-4" />
              Admin
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => navigate('/credits')}>Credits</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AddMapleKeyDialog open={addKeyOpen} onOpenChange={setAddKeyOpen} />
    </>
  );
}
